import React from 'react';

const LEVELS = [
    {
        id: 'sensors',
        title: 'Sensors',
        subtitle: 'Environmental sensing at the edge',
        description:
            'Multi-sensor cards stream humidity, temperature, light (lux), and water level so your grow space is always measured in real time.',
        icons: [
            { className: 'ri-temp-cold-line', label: 'Temperature' },
            { className: 'ri-water-percent-line', label: 'Humidity' },
            { className: 'ri-sun-line', label: 'Light' },
            { className: 'ri-water-line', label: 'Water level' },
        ],
        accent: '#4A90E2',
    },
    {
        id: 'actuators',
        title: 'Action devices',
        subtitle: 'Actuators that respond in the room',
        description:
            'Pumps, lights, fans, and valves listen to sensor data and schedules. They stay linked to the cloud for commands, care plans, and your preferences.',
        icons: [
            { className: 'ri-water-flash-line', label: 'Irrigation' },
            { className: 'ri-lightbulb-line', label: 'Lighting' },
            { className: 'ri-windy-line', label: 'Airflow' },
            { className: 'ri-cloud-line', label: 'Cloud link' },
        ],
        accent: '#29BF00',
    },
    {
        id: 'cloud',
        title: 'Cloud platform',
        subtitle: 'Ingest, time-series, and notify',
        description:
            'MQTT (e.g. Mosquitto), Telegraf, and InfluxDB compile live and historical data. The stack stores the full story of sensor ↔ action interactions and pushes notifications when devices run.',
        icons: [
            { className: 'ri-base-station-line', label: 'MQTT' },
            { className: 'ri-exchange-line', label: 'Telegraf' },
            { className: 'ri-database-2-line', label: 'InfluxDB' },
            { className: 'ri-notification-3-line', label: 'Alerts' },
        ],
        accent: '#7C6FD6',
    },
    {
        id: 'agents',
        title: 'AI agents',
        subtitle: 'A watchful layer over the stack',
        description:
            'Agents scan for anomalies, stale data, disconnects, and moments that need attention. They can propose or carry out actions with clear visibility, aligned with how you want to run your garden.',
        icons: [
            { className: 'ri-eye-line', label: 'Monitoring' },
            { className: 'ri-alarm-warning-line', label: 'Anomalies' },
            { className: 'ri-flashlight-line', label: 'Action' },
            { className: 'ri-robot-line', label: 'Automation' },
        ],
        accent: '#E8A838',
    },
    {
        id: 'user',
        title: 'You & the app',
        subtitle: 'Direction, visibility, and control',
        description:
            'The dashboard turns streams into insight: you see history and live state, send intent back through the cloud, and steer automations together with agents—planning routines and surveying every asset in one place.',
        icons: [
            { className: 'ri-dashboard-3-line', label: 'Dashboard' },
            { className: 'ri-line-chart-line', label: 'Insight' },
            { className: 'ri-send-plane-2-line', label: 'Commands' },
            { className: 'ri-plant-line', label: 'Your grow' },
        ],
        accent: '#48c774',
    },
];

/** LEVELS is sensors→user; reversed for layout so the apex (user) is at the top and the base (sensors) is widest at the bottom. */
const LEVELS_PYRAMID_TOP_DOWN = [...LEVELS].reverse();

/**
 * Animated “pyramid” overview of the Canopy stack for the About page.
 * Styling lives in about.css (about-pyramid-*).
 */
export default function CanopyArchitecturePyramid() {
    return (
        <section
            className="about-section about-pyramid-section"
            aria-labelledby="about-pyramid-heading"
        >
            <h2 id="about-pyramid-heading" className="about-section-title">
                The Canopy Stack
            </h2>
            <p className="about-pyramid-intro">
                A full perspective on how data moves from the grow space through the cloud to you—with
                intelligent agents watching the whole path.
            </p>

            <div className="about-pyramid" role="list">
                {LEVELS_PYRAMID_TOP_DOWN.map((level, index) => (
                    <React.Fragment key={level.id}>
                        {index > 0 && (
                            <div className="about-pyramid-connector" aria-hidden="true">
                                <span className="about-pyramid-connector-line" />
                                <span className="about-pyramid-connector-glow" />
                            </div>
                        )}
                        <article
                            className={`about-pyramid-level about-pyramid-level--${level.id}`}
                            role="listitem"
                            data-tier={index}
                            style={{ '--pyramid-tier-accent': level.accent }}
                        >
                            <div className="about-pyramid-level-inner">
                                <header className="about-pyramid-level-header">
                                    <h3 className="about-pyramid-level-title">{level.title}</h3>
                                    <p className="about-pyramid-level-subtitle">{level.subtitle}</p>
                                </header>
                                <div className="about-pyramid-icons" aria-hidden="true">
                                    {level.icons.map((icon) => (
                                        <span key={icon.label} className="about-pyramid-icon-wrap" title={icon.label}>
                                            <i className={icon.className} aria-hidden="true" />
                                            <span className="about-pyramid-icon-label">{icon.label}</span>
                                        </span>
                                    ))}
                                </div>
                                <p className="about-pyramid-copy">{level.description}</p>
                            </div>
                        </article>
                    </React.Fragment>
                ))}
            </div>
        </section>
    );
}
